import React from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';
import StatCard, { StatCardRow } from '../StatCard';
import { OpenExternalIcon } from '../icons';
import { formatDate } from '../../utils/dateUtils';
import { fmtMoney } from '../../utils/campaignFinanceUtils';
import ReportProblemLink from '../report/ReportProblemLink';
import {
  appFromPath,
  committeeTypeLabel,
  isCandidateCommittee,
  measureHref,
  personHref,
  TRACER_COMMITTEE_SEARCH_URL,
  type CommitteeApp,
  type CommitteeBrief,
} from './committeeTypes';

export interface CommitteeCardProps {
  isOpen: boolean;
  onClose: () => void;
  /** TRACER co_id. Without it the card resolves `name` (exact match, current or former). */
  coId?: string | null;
  /** The name as the opening surface shows it — the title until the brief loads. */
  name: string;
  /**
   * A brief already in hand (the /design-system demo, tests). Skips the fetch.
   * `null` renders the unresolved state.
   */
  brief?: CommitteeBrief | null;
  /** Overrides the app detected from the route (demo only). */
  app?: CommitteeApp;
  /** The measure the card was opened from; its row is marked "This measure". */
  contextMeasureId?: string;
}

/**
 * Plain axios, not the shared client: the card opens for signed-out readers too,
 * and the shared client adds an auth header and redirects on 401.
 */
async function fetchBrief(coId: string | null | undefined, name: string): Promise<CommitteeBrief | null> {
  try {
    const res = coId
      ? await axios.get(`/api/campaign-finance/committees/${encodeURIComponent(coId)}/brief`)
      : await axios.get('/api/campaign-finance/committees/brief', { params: { name } });
    return (res.data?.data as CommitteeBrief) ?? null;
  } catch (err) {
    if (axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 400)) return null;
    throw err;
  }
}

export const committeeBriefQueryKey = (coId: string | null | undefined, name: string) =>
  ['committee-brief', coId ? { coId } : { name: name.trim().toLowerCase() }] as const;

const chip = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap';

/**
 * Class sets per app. Pro uses its theme tokens; the citizen app restyles the card
 * with the citizen desktop tokens (--ink, --surface-2, --primary-ink…), which the
 * body reaches by carrying `.citizen-root.citizen-desktop.citizen-overlay` — the
 * pattern every portaled citizen overlay uses.
 */
interface CardUi {
  neutralChip: string;
  primaryChip: string;
  successChip: string;
  errorChip: string;
  eyebrow: string;
  link: string;
  section: string;
  inset: string;
  text1: string;
  text2: string;
  text3: string;
  rowHover: string;
  record: string;
  skeleton: string;
}

const PRO_UI: CardUi = {
  neutralChip: `${chip} bg-neutral-100 text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary`,
  primaryChip: `${chip} bg-primary-50 text-primary-700 dark:bg-primary-dark-100/40 dark:text-primary-dark-400`,
  successChip: `${chip} bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300`,
  errorChip: `${chip} bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-300`,
  eyebrow: 'text-xs font-bold uppercase tracking-[0.12em] text-text-tertiary dark:text-text-dark-tertiary',
  link: 'text-primary-700 underline-offset-2 hover:underline dark:text-primary-dark-400',
  section: 'border-t border-dialog-border px-5 py-4',
  inset: 'rounded-xl bg-neutral-50 px-4 py-3 dark:bg-background-dark-tertiary',
  text1: 'text-text-primary dark:text-text-dark-primary',
  text2: 'text-text-secondary dark:text-text-dark-secondary',
  text3: 'text-text-tertiary dark:text-text-dark-tertiary',
  rowHover: 'hover:bg-neutral-50 dark:hover:bg-background-dark-tertiary',
  record: 'bg-background-secondary dark:bg-background-dark-tertiary/40',
  skeleton: 'bg-neutral-100 dark:bg-background-dark-tertiary',
};

const CITIZEN_UI: CardUi = {
  neutralChip: `${chip} bg-[var(--surface-2)] text-[color:var(--ink-2)]`,
  primaryChip: `${chip} bg-[var(--accent)] text-[color:var(--accent-ink)]`,
  successChip: `${chip} bg-[var(--tone-green-bg)] text-[color:var(--tone-green-ink)]`,
  errorChip: `${chip} bg-[var(--tone-red-bg)] text-[color:var(--tone-red-ink)]`,
  eyebrow: 'text-[11px] font-extrabold uppercase tracking-[0.12em] text-[color:var(--ink-3)]',
  link: 'text-[color:var(--primary-ink)] underline-offset-2 hover:underline',
  section: 'border-t border-[color:var(--line)] px-5 py-4',
  inset: 'rounded-xl bg-[var(--surface-2)] px-4 py-3',
  text1: 'text-[color:var(--ink)]',
  text2: 'text-[color:var(--ink-2)]',
  text3: 'text-[color:var(--ink-3)]',
  rowHover: 'hover-elevate',
  record: 'bg-[var(--surface-2)]',
  skeleton: 'bg-[var(--surface-2)]',
};

function StatusChip({ brief, ui }: { brief: CommitteeBrief; ui: CardUi }) {
  if (brief.status === 'active') return <span className={ui.successChip}>Active</span>;
  if (brief.status === 'closed') {
    return <span className={ui.neutralChip}>{brief.closed_on ? `Closed ${formatDate(brief.closed_on)}` : 'Closed'}</span>;
  }
  return <span className={ui.neutralChip}>Dormant</span>;
}

function SidePill({ side, ui }: { side: 'support' | 'oppose'; ui: CardUi }) {
  return side === 'support'
    ? <span className={ui.successChip}>Supports</span>
    : <span className={ui.errorChip}>Opposes</span>;
}

function RecordCell({ label, value, ui }: { label: string; value: React.ReactNode; ui: CardUi }) {
  return (
    <div className="min-w-0">
      <dt className={`text-xs ${ui.text3}`}>{label}</dt>
      <dd className={`mt-0.5 text-sm font-medium break-words ${ui.text1}`}>{value}</dd>
    </div>
  );
}

function Skeleton({ ui }: { ui: CardUi }) {
  return (
    <div className="space-y-4 px-5 py-4" aria-busy="true" aria-label="Loading committee">
      {[60, 90, 75, 40].map((w) => (
        <div key={w} className={`h-4 animate-pulse rounded ${ui.skeleton}`} style={{ width: `${w}%` }} />
      ))}
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => <div key={i} className={`h-16 animate-pulse rounded-xl ${ui.skeleton}`} />)}
      </div>
    </div>
  );
}

/** Citizen money tiles: plain bordered tiles in the citizen type, not Pro's StatCards. */
function CitizenStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[11px] border border-[color:var(--line)] px-3 py-2.5">
      <div className="text-[21px] font-bold tabular-nums leading-tight text-[color:var(--ink)]">{value}</div>
      <div className="text-xs text-[color:var(--ink-3)]">{label}</div>
    </div>
  );
}

function measuresSummary(brief: CommitteeBrief): string | null {
  if (brief.measures.length === 0) return null;
  const years = [...new Set(brief.measures.map((m) => m.election_year).filter((y): y is number => typeof y === 'number'))];
  return years.length === 1 ? `${brief.measures.length} in ${years[0]}` : String(brief.measures.length);
}

/**
 * The committee card (PRD §5.11): what a campaign committee is, who it is for, what
 * it raised and spent, what it backs, and who funds it — opened from any committee
 * name in either app via <CommitteeLink>. Rendered in the shared `Modal` (focus
 * trap, Esc, backdrop, focus return, full-bleed sheet on phones, citizen theming).
 *
 * Copy rule (§5.9): no personal pronouns — committee, candidate and office names
 * carry every sentence.
 */
const CommitteeCard: React.FC<CommitteeCardProps> = ({ isOpen, onClose, coId, name, brief: givenBrief, app: appOverride, contextMeasureId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const app = appOverride ?? appFromPath(location.pathname);
  const citizen = app === 'citizen';
  const ui = citizen ? CITIZEN_UI : PRO_UI;
  const hasGiven = givenBrief !== undefined;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: committeeBriefQueryKey(coId, name),
    queryFn: () => fetchBrief(coId, name),
    enabled: isOpen && !hasGiven,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const brief = hasGiven ? givenBrief : data;
  const loading = !hasGiven && isLoading;
  const title = brief?.name || name;
  const resolvedId = brief?.co_id ?? coId ?? null;

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <ReportProblemLink entityType="committee" entityId={resolvedId} section="committee" pageTitle={title} />
      {app === 'pro' && brief && (
        <Button
          variant="primary"
          onClick={() => {
            onClose();
            navigate(`/campaign-finance?co_id=${encodeURIComponent(brief.co_id)}`);
          }}
        >
          Open full dashboard
        </Button>
      )}
    </div>
  );

  const typeLabel = brief ? committeeTypeLabel(brief.type) : null;
  // Chips sit at the top of the body, not in the title: the dialog is labelled
  // by the committee name alone (§5.11).
  const chips = brief ? (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="committee-chips">
      {typeLabel && <span className={ui.primaryChip}>{typeLabel}</span>}
      <StatusChip brief={brief} ui={ui} />
    </div>
  ) : null;

  let body: React.ReactNode;
  if (loading) {
    body = <Skeleton ui={ui} />;
  } else if (!hasGiven && isError) {
    body = (
      <div className={`px-5 py-4 text-sm ${ui.text2}`} role="alert">
        The committee record couldn&apos;t load.{' '}
        <button type="button" className={ui.link} onClick={() => { void refetch(); }}>Try again</button>
      </div>
    );
  } else if (!brief) {
    body = (
      <div className={`space-y-3 px-5 py-4 text-sm ${ui.text2}`}>
        <p>We couldn&apos;t match this name to a committee filing.</p>
        <a href={TRACER_COMMITTEE_SEARCH_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 ${ui.link}`}>
          Search committees on TRACER <OpenExternalIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    );
  } else {
    const candidateCommittee = isCandidateCommittee(brief);
    const measureCount = measuresSummary(brief);
    const recordCells: Array<[string, React.ReactNode]> = candidateCommittee
      ? [
          ['Registered with', 'Colorado Secretary of State'],
          ['Office', brief.candidate?.office || null],
          ['Registered agent', brief.registered_agent],
          brief.closed_on ? ['Closed', formatDate(brief.closed_on)] : ['Registered', brief.registered_on ? formatDate(brief.registered_on) : null],
        ]
      : [
          ['Registered with', 'Colorado Secretary of State'],
          ['Registered', brief.registered_on ? formatDate(brief.registered_on) : null],
          ['Registered agent', brief.registered_agent],
          ['Measures', measureCount],
        ];
    const shownCells = recordCells.filter(([, v]) => v != null && v !== '');

    body = (
      <div data-testid="committee-card-body">
        <div className="space-y-3 px-5 py-4">
          {chips}
          {brief.candidate && (
            <p className={`text-sm ${ui.text2}`}>
              For{' '}
              <Link to={personHref(app, brief.candidate.person_id)} onClick={onClose} className={`font-semibold ${ui.link}`}>
                {brief.candidate.name || 'the candidate'}
              </Link>
              {brief.candidate.office ? <> · {brief.candidate.office}</> : null}
            </p>
          )}
          {brief.purpose && (
            <div className={ui.inset}>
              <div className={ui.eyebrow}>Purpose</div>
              <p className={`mt-1 text-sm ${ui.text1}`}>{brief.purpose}</p>
            </div>
          )}
        </div>

        <div className={ui.section}>
          {citizen ? (
            <div className="grid grid-cols-3 gap-2">
              <CitizenStat label="Raised" value={fmtMoney(brief.totals.raised)} />
              <CitizenStat label="Spent" value={fmtMoney(brief.totals.spent)} />
              <CitizenStat label="Money left" value={fmtMoney(brief.totals.cash_on_hand)} />
            </div>
          ) : (
            <StatCardRow minTileWidth="8rem">
              <StatCard label="Raised" value={fmtMoney(brief.totals.raised)} variant="success" />
              <StatCard label="Spent" value={fmtMoney(brief.totals.spent)} />
              <StatCard label="Money left" value={fmtMoney(brief.totals.cash_on_hand)} variant="info" />
            </StatCardRow>
          )}
          {(brief.cycle || brief.as_of) && (
            <p className={`mt-2 text-xs ${ui.text3}`}>
              {[brief.cycle ? `${brief.cycle} election cycle` : null, brief.as_of ? `as of ${formatDate(brief.as_of)}` : null]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {brief.measures.length > 0 && (
          <div className={ui.section}>
            <h3 className={ui.eyebrow}>Ballot measures · {brief.measures.length}</h3>
            <ul className="mt-2 space-y-1">
              {brief.measures.map((m) => (
                <li key={m.id}>
                  <Link
                    to={measureHref(app, m.id)}
                    onClick={onClose}
                    className={`flex min-h-[44px] items-center justify-between gap-3 rounded-lg px-2 py-1.5 ${ui.rowHover}`}
                    aria-current={m.id === contextMeasureId ? 'true' : undefined}
                  >
                    <span className="min-w-0">
                      <span className={`block text-sm font-semibold ${ui.text1}`}>
                        {/* Citizens never see an SOS filing number ("#95"), only a ballot designation. */}
                        {(citizen && m.number?.startsWith('#') ? null : m.number) || 'Ballot measure'}
                        {m.id === contextMeasureId && <span className={`ml-2 font-normal ${ui.text3}`}>This measure</span>}
                      </span>
                      {m.title && <span className={`block truncate text-[13px] ${ui.text2}`}>{m.title}</span>}
                    </span>
                    <SidePill side={m.side} ui={ui} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {brief.top_donors.length > 0 && (
          <div className={ui.section}>
            <h3 className={ui.eyebrow}>Top donors</h3>
            <ul className="mt-2 space-y-2">
              {brief.top_donors.map((d, i) => (
                <li key={`${d.name}-${i}`} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0">
                    <span className={`block truncate text-sm font-semibold ${ui.text1}`}>{d.name}</span>
                    {d.city && <span className={`block text-xs ${ui.text3}`}>{d.city}</span>}
                  </span>
                  <span className={`flex-none tabular-nums text-sm font-semibold ${ui.text1}`}>{fmtMoney(d.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`${ui.section} ${ui.record}`}>
          <h3 className={ui.eyebrow}>Official record</h3>
          {shownCells.length > 0 && (
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3">
              {shownCells.map(([label, value]) => <RecordCell key={label} label={label} value={value} ui={ui} />)}
            </dl>
          )}
          <a href={brief.tracer_url} target="_blank" rel="noopener noreferrer" className={`mt-3 inline-flex items-center gap-1 text-sm ${ui.link}`}>
            Original filings with the Secretary of State <OpenExternalIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      size="md"
      className="sm:!max-w-[560px]"
      bodyClassName="min-h-0 flex-1 overflow-y-auto"
    >
      {citizen ? <div className="citizen-root citizen-desktop citizen-overlay">{body}</div> : body}
    </Modal>
  );
};

export default CommitteeCard;
